import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { catchError, map, throwError } from 'rxjs';
import { ErrorMessages, SupportedReconProvider } from '@app/shared/constants';

// Closed set mirrored from app-services `recon-audit.service.ts` — never an
// arbitrary error message, never a raw axios error. Extend only when app-services adds a code.
// 'duplicate-invoice' (Uber only) is the pre-enqueue duplicate check — see `DuplicateOfEntry`.
export type CourierInvoiceUploadRejectCode =
  | 'missing-signature-column'
  | 'unknown-column'
  | 'blank-identifier'
  | 'non-numeric-money'
  | 'unsupported-currency'
  | 'empty-file'
  | 'audit-failed'
  | 'enqueue-failed'
  | 'enqueue-unconfirmed'
  | 'too-many-files'
  | 'too-many-bytes'
  | 'invalid-invoice-number'
  | 'validate-unavailable'
  | 'duplicate-invoice';

// One earlier `recon_audits` row a rejected Uber file matched against, by BillID or by file name
// (see `matchedBy`). A BillID match is a hard block (no override); a fileName-only match ("every
// entry in `duplicateOf` is `matchedBy: 'fileName'`") is a warning the drawer can override with
// `allowNameMatch`.
export interface DuplicateOfEntry {
  auditId: number;
  billId: string | null;
  matchedBy: 'billId' | 'fileName';
  originalFileName: string | null;
  status: 'pending' | 'processing' | 'processed' | 'processed_with_failures';
  rowsEnqueued: number | null;
  rowsFailed: number | null;
  createdAt: string;
}

// Multi-file result shape returned only for MULTI_FILE_PROVIDERS (Uber). Every other provider's
// `POST admin/finance/invoices` still resolves `true` on success, unchanged.
export interface CourierInvoiceUploadResult {
  accepted: {
    // Stored name (`<sanitized base>-<unix seconds>-<hex>.<ext>`) — the Courier Invoices table key.
    fileName: string;
    // The client's `File.name` exactly as uploaded — what the drawer matches selected files on and
    // shows in banners.
    originalFileName: string;
    bills: { billId: string; invoiceTotal: string; rowsParsed: number }[];
    // Present only when the enqueue call returned no confirmation (network/timeout). The row(s)
    // were still created PENDING server-side — the drawer renders this under accepted as
    // "awaiting confirmation", never as rejected.
    code?: 'enqueue-unconfirmed';
  }[];
  rejected: {
    fileName: string;
    originalFileName: string;
    code: CourierInvoiceUploadRejectCode;
    // Present only for code: 'duplicate-invoice' — the matched earlier row(s) the drawer's
    // per-case warning is built from.
    duplicateOf?: DuplicateOfEntry[];
  }[];
}

// Courier Invoices list row shape (`GET admin/finance/invoices`). Loosely typed at the call site
// today (`getInvoices` stays `any`) — this documents the fields the FILE column and the Retry
// button read: `id` (retry route target) and `original_file_name` (FILE column / detail title,
// null on legacy rows uploaded before this field existed).
export interface CourierInvoice {
  id: number;
  file_name: string;
  original_file_name?: string | null;
  courier: string;
  invoice_number: string;
  invoice_amount: string;
  status: string;
  created_at: string;
  updated_at: string;
  rows_parsed?: number;
  rows_enqueued?: number;
  rows_failed?: number;
  // Absent for SFTP rows and for runs finalized before these counts existed.
  rows_done?: number;
  rows_review?: number;
  providerName?: string;
}

// Human labels for the drawer's per-rejected-file warning line (`<fileName>: <label>`).
// 'duplicate-invoice' is never rendered through this label in practice — the drawer renders its
// own per-case message from `duplicateOf` instead — kept here as the required fallback for the
// exhaustive `Record`.
export const COURIER_INVOICE_REJECT_LABELS: Record<CourierInvoiceUploadRejectCode, string> = {
  'missing-signature-column': 'Missing required column',
  'unknown-column': 'Unrecognized column in file',
  'blank-identifier': 'A required identifier (e.g. BillID) is blank',
  'non-numeric-money': 'A money column contains a non-numeric value',
  'unsupported-currency': 'Unsupported currency',
  'empty-file': 'File has no data rows',
  'audit-failed': 'Could not save the invoice records for this file',
  'enqueue-failed': 'Could not queue this file for processing',
  'enqueue-unconfirmed': 'Awaiting confirmation',
  'too-many-files': 'Too many files in this upload',
  'too-many-bytes': 'Upload exceeds the total size limit',
  'invalid-invoice-number': 'Invalid invoice number',
  'validate-unavailable': 'Invoice validation service unavailable – try again',
  'duplicate-invoice': 'This invoice was already uploaded',
};

export type InvoiceAuditSectionName = 'shipments' | 'unknownShipments' | 'failedRows' | 'siblings';
export type InvoiceAuditSectionState = 'ok' | 'unavailable';

export interface CourierInvoiceSibling {
  id: number;
  invoice_number: string;
  status: CourierInvoice['status'];
  rows_parsed?: number;
  rows_failed?: number;
  rows_done?: number;
  rows_review?: number;
}

// Mirrors app-services' InvoiceAuditSummary: run-time counts are null when never recorded; the
// open/logged/found counts are null when their section could not be loaded.
export interface InvoiceAuditSummary {
  rowsParsed: number | null;
  rowsEnqueued: number | null;
  rowsSkipped: number | null;
  rowsDone: number | null;
  rowsReview: number | null;
  rowsFailed: number | null;
  reviewOpen: number | null;
  failedOpen: number | null;
  failedLogged: number | null;
  shipmentsFound: number | null;
}

// Mirrors app-services' ReconAuditFailedRowRO; `resolvedAt` is the only state field.
export interface ReconAuditFailedRow {
  id: number;
  member: string;
  identifier: string | null;
  invoiceNumber: string | null;
  amount: number | null;
  reason: string | null;
  rawRow: Record<string, unknown> | null;
  attempts: number;
  firstFailedAt: string;
  lastFailedAt: string;
  resolvedAt: string | null;
}

export interface CourierInvoiceAuditDetail {
  invoice: CourierInvoice;
  trackingAvailable: boolean;
  siblings: CourierInvoiceSibling[];
  summary: InvoiceAuditSummary | null;
  shipments: any[];
  totalShipments: number;
  unknownShipments: any[];
  unknownTrackingRestricted: boolean;
  failedRows: ReconAuditFailedRow[];
  // Absent on an older backend and on the legacy route: every section reads as 'ok' then.
  sections?: Record<InvoiceAuditSectionName, InvoiceAuditSectionState>;
  // Set by the page, never by the API: the payload came from a bookmarked `<name>_<date>` link,
  // which carries the header (possibly none) and shipments but no row-level outcomes.
  legacy?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FinanceService {
  constructor(private http: HttpClient) {}

  public getInvoices(params: any): any {
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/finance/invoices`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }

  public uploadCourierInvoice(formData: any): Observable<boolean | CourierInvoiceUploadResult> {
    return this.http
      .post<boolean | CourierInvoiceUploadResult>(`${environment.APP_SERVICES_URL}/admin/finance/invoices`, formData)
      .pipe(
        map((res: boolean | CourierInvoiceUploadResult) => {
          return res;
        }),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }

  // Uber only — re-runs just the failed rows of an already-`processed_with_failures` invoice
  // in place (same audit row, no re-upload). app-services enforces the real preconditions
  // (courier, status, rows_failed, 30-day window) and returns 400 `{ message, reason }` or 409
  // when they aren't met; `code: 'enqueue-unconfirmed'` means the row flipped to `processing` but
  // the enqueue call itself wasn't confirmed.
  public retryFailedRows(invoiceId: number): Observable<{ status: 'processing'; code?: 'enqueue-unconfirmed' }> {
    return this.http
      .post<{ status: 'processing'; code?: 'enqueue-unconfirmed' }>(
        `${environment.APP_SERVICES_URL}/admin/finance/invoices/${invoiceId}/retry-failed`,
        {}
      )
      .pipe(
        map((res) => res),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }

  public triggerRecon(provider: SupportedReconProvider, fileName: string): any {
    return this.http
      .post(`${environment.APP_SERVICES_URL}/admin/finance/invoices/trigger-recon`, { provider, fileName })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }

  public getCourierInvoiceShipments(courierInvoiceName): any {
    return this.http.get(`${environment.APP_SERVICES_URL}/admin/finance/invoices/${courierInvoiceName}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  public getCourierInvoiceAuditDetail(id: number): Observable<CourierInvoiceAuditDetail> {
    return this.http
      .get<CourierInvoiceAuditDetail>(`${environment.APP_SERVICES_URL}/admin/finance/invoice-audits/${id}`)
      .pipe(catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED)));
  }

  public getMAInvoiceShipments(courierInvoiceNumber): any {
    return this.http.get(`${environment.APP_SERVICES_URL}/admin/finance/machool-invoices/${courierInvoiceNumber}`).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
    );
  }

  public getNoCourierInvoiceShipments(params): any {
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/finance/no-courier-invoice`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }

  public getNoMAInvoiceShipments(params): any {
    return this.http
      .get(`${environment.APP_SERVICES_URL}/admin/finance/no-machool-invoice`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: any) => throwError(error.error || ErrorMessages.UNEXPECTED))
      );
  }
}
